import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, User, Mail, Lock, ArrowRight, Loader2, AlertCircle, CheckCircle, Briefcase, Users } from 'lucide-react';

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [isLoginMode, setIsLoginMode] = useState(true);
  
  const [role, setRole] = useState('studente'); 
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false); 
  
  const [message, setMessage] = useState({ type: '', text: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const checkSession = async () => { 
        const { data: { session } } = await supabase.auth.getSession(); 
        if (session) navigate('/', { replace: true }); 
    };
    checkSession();
  }, [navigate]);

  const validateForm = () => {
      if (!isLoginMode) {
          if (!acceptedTerms) return "Devi accettare i Termini e Condizioni.";
          if (!acceptedPrivacy) return "Devi accettare la Privacy Policy.";
          
          if (role === 'studente' && !email.endsWith('@studenti.unitus.it')) {
              return "Gli studenti devono usare la mail @studenti.unitus.it";
          }
          
          if (role === 'azienda' && !companyName.trim()) return "Inserisci il nome dell'azienda.";
          if (role !== 'azienda' && !username.trim()) return "Inserisci un username.";
          if (password.length < 6) return "La password deve essere di almeno 6 caratteri.";
      }
      return null;
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    
    const errorMsg = validateForm();
    if (errorMsg) {
        setMessage({ type: 'error', text: errorMsg });
        return;
    }

    setLoading(true);
    
    try {
      if (isLoginMode) {
        // --- LOGIN ---
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // Redirect immediato dopo login
        navigate('/', { replace: true });
      } else {
        // --- REGISTRAZIONE ---
        const { error } = await supabase.auth.signUp({
          email, 
          password,
          options: { 
            data: { 
                username: role === 'azienda' ? companyName : username, 
                role: role, 
                company_name: companyName 
            } 
          }
        });
        
        if (error) throw error;
        
        // Gestione post-registrazione differenziata
        if (role === 'azienda') {
            setMessage({ type: 'success', text: 'Registrazione Azienda effettuata! In attesa di approvazione.' });
            // Ritardo per far leggere il messaggio all'azienda
            setTimeout(() => navigate('/', { replace: true }), 2000); 
        } else {
            // Studenti/Privati: Redirect IMMEDIATO (senza messaggio o timeout)
            navigate('/', { replace: true });
        }
      }
    } catch (error) { 
        const msg = error.message === 'Invalid login credentials' ? 'Email o password errati.' : error.message;
        setMessage({ type: 'error', text: msg }); 
    } finally { 
        setLoading(false); 
    }
  };

  const handleRoleChange = (newRole) => {
      setRole(newRole);
      setMessage({ type: '', text: '' });
  };

  const toggleMode = () => {
      setIsLoginMode(!isLoginMode);
      setMessage({ type: '', text: '' });
      setAcceptedTerms(false);
      setAcceptedPrivacy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[120px] animate-pulse"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-pink-100 rounded-full blur-[120px]"></div>
      
      <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-slate-200 border border-slate-100 w-full max-w-lg overflow-hidden relative z-10 flex flex-col">
        <div className="p-8 md:p-12 w-full">
            <div className="text-center mb-8">
                <Link to="/" className="inline-flex items-center gap-2 text-slate-900 font-bold text-xl mb-3 hover:opacity-80 transition"><BookOpen className="h-6 w-6 text-yellow-500" /> TusciaHub</Link>
                <h2 className="text-3xl font-black text-slate-900">{isLoginMode ? 'Bentornato' : 'Crea Account'}</h2>
            </div>
            
            {!isLoginMode && (
                <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
                    {[
                        {id:'studente', label:'Studente', icon:User}, 
                        {id:'privato', label:'Privato', icon:Users}, 
                        {id:'azienda', label:'Azienda', icon:Briefcase} 
                    ].map(tab => (
                        <button key={tab.id} type="button" onClick={() => handleRoleChange(tab.id)} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-bold rounded-lg transition-all ${role===tab.id?'bg-white text-indigo-600 shadow-sm':'text-slate-500 hover:text-slate-700'}`}>
                            <tab.icon size={14}/>{tab.label}
                        </button>
                    ))}
                </div>
            )}

            {message.text && (
                <div className={`p-4 mb-6 rounded-xl flex items-start gap-3 text-sm font-bold animate-in fade-in ${message.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'}`}>
                    {message.type === 'error' ? <AlertCircle size={18} className="shrink-0 mt-0.5"/> : <CheckCircle size={18} className="shrink-0 mt-0.5"/>} 
                    {message.text}
                </div>
            )}
            
            <form onSubmit={handleAuth} className="space-y-5">
                
                {!isLoginMode && role === 'azienda' && (
                    <div className="relative group">
                        <Briefcase className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition" />
                        <input type="text" placeholder="Nome Azienda" className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition font-bold text-slate-800 placeholder:text-slate-400" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
                    </div>
                )}

                {!isLoginMode && role !== 'azienda' && (
                    <div className="relative group">
                        <User className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition" />
                        <input type="text" placeholder="Username" className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition font-bold text-slate-800 placeholder:text-slate-400" value={username} onChange={(e) => setUsername(e.target.value)} required />
                    </div>
                )}
                
                <div className="relative group">
                    <Mail className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition" />
                    <input type="email" placeholder={!isLoginMode && role === 'studente' ? "mario.rossi@studenti.unitus.it" : "Email"} className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition font-bold text-slate-800 placeholder:text-slate-400" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                
                <div className="relative group">
                    <Lock className="absolute left-4 top-3.5 h-5 w-5 text-slate-400 group-focus-within:text-indigo-600 transition" />
                    <input type="password" placeholder="Password" className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-50 border-transparent focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none transition font-bold text-slate-800 placeholder:text-slate-400" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                
                {!isLoginMode && (
                    <div className="space-y-3 px-1 mt-2">
                        <div className="flex items-start gap-3">
                            <input type="checkbox" id="terms" className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />
                            <label htmlFor="terms" className="text-xs text-slate-500 leading-relaxed cursor-pointer select-none">
                                Accetto i <Link to="/terms" target="_blank" className="text-indigo-600 font-bold hover:underline">Termini e Condizioni</Link> del servizio.
                            </label>
                        </div>
                        <div className="flex items-start gap-3">
                            <input type="checkbox" id="privacy" className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" checked={acceptedPrivacy} onChange={(e) => setAcceptedPrivacy(e.target.checked)} />
                            <label htmlFor="privacy" className="text-xs text-slate-500 leading-relaxed cursor-pointer select-none">
                                Ho preso visione della <Link to="/privacy" target="_blank" className="text-indigo-600 font-bold hover:underline">Privacy Policy</Link>.
                            </label>
                        </div>
                    </div>
                )}
                
                {isLoginMode && (
                    <div className="text-right">
                        <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-indigo-600 transition font-bold">Password dimenticata?</Link>
                    </div>
                )}
                
                <button disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-2xl transition-all shadow-xl shadow-indigo-600/20 flex justify-center items-center gap-2 mt-4 hover:-translate-y-1">
                    {loading ? <Loader2 className="animate-spin" /> : (isLoginMode ? 'Accedi' : 'Registrati')} 
                    {!loading && <ArrowRight size={20} />}
                </button>
            </form>
            
            <div className="mt-10 text-center">
                <p className="text-sm text-slate-500 font-medium">
                    {isLoginMode ? "Non hai un account?" : "Hai già un account?"} 
                    <button onClick={toggleMode} className="ml-1 text-indigo-600 font-bold hover:text-indigo-800 transition">
                        {isLoginMode ? "Registrati" : "Accedi"}
                    </button>
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};
export default Auth;